import type { Survivor, SurvivorAppearance, SurvivorAppearanceRecipe } from "../types";
import { motionForActivity } from "../lib/survivorMotion";

export interface OutfitPalette {
  primary: string;
  secondary: string;
  accent: string;
  shadow: string;
}

export const skinTones: Record<string, { base: string; light: string; shadow: string; line: string }> = {
  porcelain: { base: "#d7b6a0", light: "#efd2bd", shadow: "#9b7262", line: "#61483e" },
  fair: { base: "#c99d80", light: "#e8bea1", shadow: "#8c624f", line: "#594036" },
  olive: { base: "#a77a58", light: "#c79b73", shadow: "#704c38", line: "#49352b" },
  bronze: { base: "#936047", light: "#b87d5c", shadow: "#603d31", line: "#3f2c26" },
  brown: { base: "#754a36", light: "#9c694b", shadow: "#4b3028", line: "#31231f" },
  deep: { base: "#563527", light: "#80533c", shadow: "#35231e", line: "#241a17" },
};

export const hairColours: Record<string, { base: string; light: string; shadow: string }> = {
  black: { base: "#181817", light: "#3a3732", shadow: "#080909" },
  dark_brown: { base: "#30221d", light: "#5a4033", shadow: "#15110f" },
  auburn: { base: "#5b3023", light: "#8a4c32", shadow: "#271916" },
  ash_blonde: { base: "#82745e", light: "#b4a487", shadow: "#443e35" },
  grey: { base: "#696d68", light: "#a8aaa3", shadow: "#343936" },
};

export const outfitPalettes: Record<string, OutfitPalette> = {
  moss_rust: { primary: "#40564b", secondary: "#273a35", accent: "#b45f3f", shadow: "#18231f" },
  slate_amber: { primary: "#45545a", secondary: "#29343a", accent: "#c28a45", shadow: "#192126" },
  navy_ochre: { primary: "#374a58", secondary: "#202e38", accent: "#c29850", shadow: "#161f25" },
  burgundy_steel: { primary: "#624047", secondary: "#353f45", accent: "#b9a477", shadow: "#251a1e" },
  forest_charcoal: { primary: "#394b3d", secondary: "#252d2b", accent: "#8e7650", shadow: "#161c19" },
};

const legacyRecipes: SurvivorAppearanceRecipe[] = [
  { body: "average", skin: "bronze", face: "heart", hair: "bun", hair_colour: "black", outfit: "utility", outfit_palette: "moss_rust", accessories: [], markings: [] },
  { body: "tall", skin: "fair", face: "long", hair: "waves", hair_colour: "dark_brown", outfit: "workwear", outfit_palette: "slate_amber", accessories: ["glasses"], markings: [] },
  { body: "lean", skin: "deep", face: "oval", hair: "braids", hair_colour: "black", outfit: "field_jacket", outfit_palette: "navy_ochre", accessories: ["scarf"], markings: [] },
  { body: "compact", skin: "olive", face: "round", hair: "bob", hair_colour: "auburn", outfit: "scavenger", outfit_palette: "burgundy_steel", accessories: ["radio"], markings: ["freckles"] },
];

export function resolvedAppearance(survivor: Survivor): SurvivorAppearance {
  if (survivor.appearance?.recipe) return survivor.appearance;
  const index = Math.abs(survivor.portrait_variant ?? 0) % legacyRecipes.length;
  return {
    version: 1,
    origin: "procedural",
    profile_id: null,
    recipe: legacyRecipes[index]!,
    motion_phase: (index * 211 + survivor.name.length * 17) % 1000,
  };
}

export function skinFor(recipe: SurvivorAppearanceRecipe) {
  return skinTones[recipe.skin] ?? skinTones.olive!;
}

export function hairFor(recipe: SurvivorAppearanceRecipe) {
  return hairColours[recipe.hair_colour] ?? hairColours.dark_brown!;
}

export function outfitFor(recipe: SurvivorAppearanceRecipe): OutfitPalette {
  return outfitPalettes[recipe.outfit_palette] ?? outfitPalettes.forest_charcoal!;
}

export function hasAppearanceDetail(recipe: SurvivorAppearanceRecipe, detail: string): boolean {
  return recipe.accessories.includes(detail) || recipe.markings.includes(detail);
}

export function safeArtToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_-]/g, "-");
}

export function survivorAssetUrl(filename: string, baseUrl = import.meta.env.BASE_URL): string {
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const safePath = filename
    .replace(/^[/\\]+/, "")
    .split(/[/\\]+/)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `${base}assets/survivors/${safePath}`;
}

export type SurvivorFigurePose = "idle" | "travel" | "labour" | "precision" | "guard" | "rest";
export type SurvivorFigureArtMode = "pose-backed" | "rig-only";

export interface SurvivorRasterProfile {
  portraitHero: string;
  portraitThumb?: string;
  figureArtMode: SurvivorFigureArtMode;
  figures?: Partial<Record<SurvivorFigurePose, string>>;
  rigAtlas?: string;
}

function profileAssets(profileId: string) {
  const root = `v3/profiles/${profileId}`;
  return {
    portraitHero: survivorAssetUrl(`${root}/portrait-hero.webp`),
    portraitThumb: survivorAssetUrl(`${root}/portrait-thumb.webp`),
    rigAtlas: survivorAssetUrl(`v4/profiles/${profileId}/rig-atlas.webp`),
  };
}

function poseBackedProfile(profileId: string): SurvivorRasterProfile {
  const root = `v3/profiles/${profileId}`;
  const poses: SurvivorFigurePose[] = ["idle", "labour", "guard"];
  return {
    ...profileAssets(profileId),
    figureArtMode: "pose-backed",
    figures: Object.fromEntries(
      poses.map((pose) => [pose, survivorAssetUrl(`${root}/figure-${pose}.webp`)]),
    ),
  };
}

function rigOnlyProfile(profileId: string): SurvivorRasterProfile {
  return {
    ...profileAssets(profileId),
    figureArtMode: "rig-only",
  };
}

export const survivorRasterArt: Partial<Record<string, SurvivorRasterProfile>> = {
  "mara-voss": poseBackedProfile("mara-voss"),
  "jonah-reed": poseBackedProfile("jonah-reed"),
  "inez-okafor": poseBackedProfile("inez-okafor"),
  "cal-mercer": poseBackedProfile("cal-mercer"),
  "dev-malik": poseBackedProfile("dev-malik"),
  "ruth-bell": poseBackedProfile("ruth-bell"),
  "asha-sayeed": rigOnlyProfile("asha-sayeed"),
  "leoni-price": rigOnlyProfile("leoni-price"),
  "gwen-parry": rigOnlyProfile("gwen-parry"),
  "kit-hargreaves": rigOnlyProfile("kit-hargreaves"),
  "pavel-novak": rigOnlyProfile("pavel-novak"),
  "samira-shah": rigOnlyProfile("samira-shah"),
};

export const fallbackSurvivorArt = {
  portrait: survivorAssetUrl("v3/fallback/portrait-hero.webp"),
  figure: survivorAssetUrl("v3/fallback/figure-idle.webp"),
  injuredOverlay: survivorAssetUrl("v3/overlays/portrait-injured.webp"),
  figureInjuredOverlay: survivorAssetUrl("v3/overlays/figure-injured.webp"),
};

export function portraitArtFor(survivor: Survivor, detail: "hero" | "thumb" = "hero"): string {
  const profileId = resolvedAppearance(survivor).profile_id;
  const profile = profileId ? survivorRasterArt[profileId] : undefined;
  if (!profile) return fallbackSurvivorArt.portrait;
  return detail === "thumb" && profile.portraitThumb ? profile.portraitThumb : profile.portraitHero;
}

export function figurePoseForActivity(activity: string): SurvivorFigurePose {
  const motion = motionForActivity(activity);
  if (motion === "walk" || motion === "climb") return "travel";
  if (motion === "labour" || motion === "carry") return "labour";
  if (motion === "precision") return "precision";
  if (motion === "guard") return "guard";
  if (motion === "rest") return "rest";
  return "idle";
}

export function rigAtlasFor(survivor: Survivor): string | null {
  const profileId = resolvedAppearance(survivor).profile_id;
  if (!profileId) return null;
  return survivorRasterArt[profileId]?.rigAtlas ?? null;
}

export function figureArtModeFor(survivor: Survivor): SurvivorFigureArtMode | null {
  const profileId = resolvedAppearance(survivor).profile_id;
  if (!profileId) return null;
  return survivorRasterArt[profileId]?.figureArtMode ?? null;
}

export function figureArtFor(
  survivor: Survivor,
  activity: string,
): { source: string; pose: SurvivorFigurePose } {
  const pose = figurePoseForActivity(activity);
  const profileId = resolvedAppearance(survivor).profile_id;
  const profile = profileId ? survivorRasterArt[profileId] : undefined;
  return {
    source: profile?.figures?.[pose] ?? profile?.figures?.idle ?? fallbackSurvivorArt.figure,
    pose,
  };
}
